/**
 * Wall & Gate Generation
 * Defensive structures for towns and cities.
 */

import type { Wall, Gate, District, EntryPoint, Point } from './types';
import { SeededRandom } from './random';
import {
  convexHull,
  offsetPolygon,
  distance,
  angleBetween,
  fromPolar,
  smoothPolyline,
} from './geometry';

// ============================================================================
// WALL GENERATION
// ============================================================================

export function generateWalls(
  districts: District[],
  entryPoints: EntryPoint[],
  hasInnerWall: boolean,
  rng: SeededRandom,
): { walls: Wall[]; gates: Gate[] } {
  const walls: Wall[] = [];
  const gates: Gate[] = [];
  let wallIdx = 0;
  let gateIdx = 0;

  // Outer wall: hull around all non-farmland districts
  const innerDistricts = districts.filter(d => d.type !== 'farmland');
  if (innerDistricts.length === 0) return { walls, gates };

  const allBoundaryPoints: Point[] = [];
  for (const d of innerDistricts) {
    allBoundaryPoints.push(...d.boundary);
  }

  const hull = convexHull(allBoundaryPoints);
  // Offset outward slightly and add irregularity
  const outerWallPoints = offsetPolygon(hull, 8 + rng.range(0, 5));

  // Close the loop
  const outerLoop = [...outerWallPoints, outerWallPoints[0]];
  const smoothedOuter = smoothPolyline(outerLoop, 1);

  walls.push({
    id: `wall_${wallIdx++}`,
    points: smoothedOuter,
    tier: 'outer',
  });

  // Place gates where entry roads approach the wall
  for (const entry of entryPoints) {
    const gatePos = findClosestPointOnPolyline(entry.position, smoothedOuter);
    if (gatePos) {
      gates.push({
        id: `gate_${gateIdx++}`,
        position: gatePos,
        wallId: walls[0].id,
        rotation: angleBetween(gatePos, { x: 0, y: 0 }),
        entryPointId: entry.id,
      });
    }
  }

  // Inner wall: hull around center + noble + temple districts
  if (hasInnerWall) {
    const coreDistricts = districts.filter(
      d => d.type === 'center' || d.type === 'noble' || d.type === 'temple'
    );
    if (coreDistricts.length >= 2) {
      const corePoints: Point[] = [];
      for (const d of coreDistricts) {
        corePoints.push(...d.boundary);
      }
      const innerHull = convexHull(corePoints);
      const innerWallPoints = offsetPolygon(innerHull, 5 + rng.range(0, 3));
      const innerLoop = [...innerWallPoints, innerWallPoints[0]];
      const smoothedInner = smoothPolyline(innerLoop, 1);

      walls.push({
        id: `wall_${wallIdx++}`,
        points: smoothedInner,
        tier: 'inner',
      });

      // Inner wall gets 2 gates
      const innerGateAngles = [rng.range(0, Math.PI), rng.range(Math.PI, Math.PI * 2)];
      for (const angle of innerGateAngles) {
        const searchPoint = fromPolar(angle, 200);
        const gatePos = findClosestPointOnPolyline(searchPoint, smoothedInner);
        if (gatePos) {
          gates.push({
            id: `gate_${gateIdx++}`,
            position: gatePos,
            wallId: walls[walls.length - 1].id,
            rotation: angle + Math.PI,
            entryPointId: null,
          });
        }
      }
    }
  }

  return { walls, gates };
}

// ============================================================================
// HELPERS
// ============================================================================

/** Find the point on a polyline closest to a given point */
function findClosestPointOnPolyline(target: Point, polyline: Point[]): Point | null {
  let closest: Point | null = null;
  let closestDist = Infinity;

  for (let i = 0; i < polyline.length - 1; i++) {
    const a = polyline[i];
    const b = polyline[i + 1];
    const proj = projectPointOnSegment(target, a, b);
    const d = distance(target, proj);
    if (d < closestDist) {
      closestDist = d;
      closest = proj;
    }
  }

  return closest;
}

/** Project a point onto a line segment, clamped to segment bounds */
function projectPointOnSegment(p: Point, a: Point, b: Point): Point {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return a;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq));
  return { x: a.x + t * dx, y: a.y + t * dy };
}
